/**
 * Column mapping for the CSV import (section 17): the two target column sets (one per
 * template, keluarga and umat), the header aliases an Access or Excel export is likely to
 * use, the guess that pairs file columns with SATU columns, and the merge with a mapping
 * remembered from an earlier file with the same headers.
 *
 * `Mapping` is the plan's interface: `{ fileColumn, target }` with `'lewati'` for a column
 * that is not imported. File columns are identified by their header text, made unique
 * when a file repeats a header ("Nama", "Nama (2)").
 *
 * The functions ending in `Dari` take any column list (the ceremony batch's, spec 2026-10-02
 * upacara bersama, 3.4).
 *
 * Release 19 (owner, 2026-10-08): the umat template gains the parish data sheet's fields, and the
 * sheet's own headings ("Data Umat" and its Google Form's questions) map to them. The sensitive
 * ones (`khususStaf`) are read for parish staff and admins only (`MasukanBaris.denganProfil`). A
 * column of uploaded scans ("Upload KTP") is never guessed: scans are read, then deleted.
 */
import ui from '../strings/ui.id.json' with { type: 'json' }
import { isiUnit, SEBUTAN_BAWAAN, type SebutanUnit } from '../strings/unit'
import { kunciTeks } from './normalise'

export type JenisImpor = 'keluarga' | 'umat'

export const LEWATI = 'lewati'

export interface Mapping {
  fileColumn: string
  target: string | typeof LEWATI
}

export type FormatKolom =
  | 'teks'
  | 'nama'
  | 'tanggal'
  | 'lp'
  | 'angka16'
  | 'telepon'
  | 'kodePendidikan'
  | 'kodePekerjaan'
  | 'hubungan'
  | 'statusGereja'
  | 'statusPerkawinan'
  | 'statusHidup'
  | 'tempatTanggalLahir'
  | 'nomorKk'
  | 'lingkungan'
  | 'email'
  | 'jenisBaptis'
  | 'yaTidak'
  | 'agama'
  | 'anakKe'
  | 'pendidikanSaatIni'
  | 'tahun'
  | 'keterampilan'
  | 'anggotaCu'
  | 'statusRumah'
  | 'golonganDarah'
  | 'suku'
  | 'pendapatan'
  | 'asuransi'
  | 'jenisPerkawinan'
  | 'parokiSakramen'

export interface KolomTarget {
  id: string
  label: string
  wajib: boolean
  format: FormatKolom
  /** Header texts (as alias keys) that mean this column. */
  alias: readonly string[]
  /** An example value for the template's sample row. */
  contoh: string
  /**
   * Release 19: a sensitive field (`umat_profil`), read and written for parish staff and admins
   * only.
   */
  khususStaf?: boolean | undefined
}

const label = ui.impor.kolom

/**
 * "Nomor KK" (release 18, owner 2026-10-08): the government KK number when its cell is 16 digits;
 * any other value is read as the family's Kode keluarga (`bacaKolomNomorKk`).
 */
const NOMOR_KK_ALIAS = [
  'nomor kk',
  'no kk',
  'kk',
  'nomor kartu keluarga',
  'no kartu keluarga',
  'kartu keluarga',
  'nomor kk pemerintah',
  'no kk pemerintah',
  'kk pemerintah',
  'nomor kk dukcapil',
  'no kk dukcapil',
  'kk dukcapil',
  'nomor kk disdukcapil',
  'no kk disdukcapil',
  'kk disdukcapil',
  'kk sipil',
  'no kk sipil',
  'nomor kk sipil',
  'no kk negara',
  'no kk nasional',
  'nomor kk nasional',
]

/**
 * "Kode keluarga": the parish's own family code, and the headers of a parish's own numbering
 * (an older file's "Nomor KK Katolik" is read as the code; SATU gives the parish number).
 */
const KODE_ALIAS = [
  'kode keluarga',
  'kode kel',
  'kode kk',
  'nomor kk katolik',
  'no kk katolik',
  'kk katolik',
  'nomor kk gereja',
  'no kk gereja',
  'kk gereja',
  'nomor kk paroki',
  'no kk paroki',
  'nomor keluarga',
  'no keluarga',
  'no kel',
]

const TELEPON_ALIAS = [
  'telepon',
  'telp',
  'tlp',
  'hp',
  'no hp',
  'nomor hp',
  'no telepon',
  'nomor telepon',
  'no telp',
  'whatsapp',
  'wa',
  'no wa',
  'nomor wa',
  'handphone',
  'ponsel',
  'kontak',
  'nomor handphone',
  'no handphone',
  'nomor telepon whatsapp',
  'nomor whatsapp',
]

const CATATAN_ALIAS = ['catatan', 'keterangan', 'ket', 'note', 'notes', 'remarks', 'catatan lain']

/**
 * The family's housing (release 19): its own column in the family file, and in a person file the
 * sheet's "Status Rumah Tinggal", written to the person's family. Never "tempat tinggal" alone:
 * "Alamat tempat tinggal" is the address.
 */
const STATUS_RUMAH_KOLOM: KolomTarget = {
  id: 'status_rumah',
  label: label.status_rumah,
  wajib: false,
  format: 'statusRumah',
  alias: [
    'status rumah',
    'status rumah tinggal',
    'status tempat tinggal',
    'status kepemilikan rumah',
    'kepemilikan rumah',
    'status hunian',
  ],
  contoh: 'Milik sendiri',
}

/** The sacraments a person file may give from elsewhere (release 19). */
export type KunciSakramenImpor = 'baptis' | 'komuni' | 'krisma' | 'perkawinan'

/** The words a heading names each sacrament by, the longest first. */
const KATA_SAKRAMEN: Readonly<Record<KunciSakramenImpor, readonly string[]>> = {
  baptis: ['baptis', 'baptist', 'permandian'],
  komuni: ['komuni pertama', 'komuni', 'sambut baru'],
  krisma: ['krisma', 'penguatan'],
  perkawinan: ['perkawinan', 'pernikahan', 'nikah'],
}

/** The Google Form's question words: "Paroki tempat anda menerima Krisma". */
const TEMPAT_ANDA: Readonly<Record<KunciSakramenImpor, readonly string[]>> = {
  baptis: ['tempat anda dibaptis'],
  komuni: ['tempat anda menerima komuni pertama'],
  krisma: ['tempat anda menerima krisma'],
  perkawinan: ['tempat anda menerima perkawinan', 'tempat anda menikah'],
}

const CONTOH_SAKRAMEN: Readonly<Record<KunciSakramenImpor, string>> = {
  baptis: '20-06-1987',
  komuni: '',
  krisma: '',
  perkawinan: '',
}

/**
 * A sacrament received elsewhere (release 19): its date, the certificate's number, the church and
 * the parish, written into `referensi_sakramen_luar`; for a baptism and a marriage their kind.
 */
function kolomSakramen(k: KunciSakramenImpor): KolomTarget[] {
  const kata = KATA_SAKRAMEN[k]
  const tempat = TEMPAT_ANDA[k]
  const dari = (pola: readonly string[]) => kata.flatMap((w) => pola.map((p) => `${p} ${w}`))
  const kolom: KolomTarget[] = [
    {
      id: `${k}_tanggal`,
      label: label[`${k}_tanggal`],
      wajib: false,
      format: 'tanggal',
      alias: [
        ...dari(['tanggal', 'tgl', 'tahun']),
        ...(k === 'baptis' ? ['tanggal dibaptis'] : []),
      ],
      contoh: CONTOH_SAKRAMEN[k],
    },
    {
      id: `${k}_nomor_surat`,
      label: label[`${k}_nomor_surat`],
      wajib: false,
      format: 'teks',
      alias: dari([
        'nomor surat',
        'no surat',
        'surat',
        'nomor akta',
        'no akta',
        'nomor sertifikat',
        'no sertifikat',
      ]),
      contoh: '',
    },
    {
      id: `${k}_gereja`,
      label: label[`${k}_gereja`],
      wajib: false,
      format: 'teks',
      alias: [
        ...dari(['gereja', 'nama gereja']),
        ...tempat.flatMap((t) => [`gereja ${t}`, `nama gereja ${t}`]),
      ],
      contoh: '',
    },
    {
      id: `${k}_paroki`,
      label: label[`${k}_paroki`],
      wajib: false,
      format: 'parokiSakramen',
      alias: [...dari(['paroki', 'nama paroki']), ...tempat.map((t) => `paroki ${t}`)],
      contoh: k === 'baptis' ? 'Paroki Ewer' : '',
    },
  ]
  if (k === 'baptis') {
    kolom.push({
      id: 'jenis_baptis',
      label: label.jenis_baptis,
      wajib: false,
      format: 'jenisBaptis',
      alias: ['jenis baptis', 'jenis permandian', 'jenis pembaptisan'],
      contoh: 'Bayi',
    })
  }
  if (k === 'perkawinan') {
    kolom.push({
      id: 'jenis_perkawinan',
      label: label.jenis_perkawinan,
      wajib: false,
      format: 'jenisPerkawinan',
      alias: ['jenis perkawinan', 'jenis pernikahan', 'jenis nikah', 'bentuk perkawinan'],
      contoh: '',
    })
  }
  return kolom
}

export const KOLOM_KELUARGA: readonly KolomTarget[] = [
  {
    // Required unless "Belum punya KK Disdukcapil" is ticked at mapping (`periksaPemetaan`).
    id: 'nomor_kk',
    label: label.nomor_kk,
    wajib: true,
    format: 'nomorKk',
    alias: NOMOR_KK_ALIAS,
    // Never 16 digits in a template (no NIK-like example): the format column says it.
    contoh: '16 angka',
  },
  {
    id: 'kode_keluarga',
    label: label.kode_keluarga,
    wajib: false,
    format: 'teks',
    alias: KODE_ALIAS,
    contoh: 'AP-001',
  },
  {
    id: 'lingkungan',
    label: label.lingkungan,
    wajib: true,
    format: 'lingkungan',
    alias: [
      'lingkungan',
      'lingk',
      'ling',
      'lk',
      'nama lingkungan',
      'kombas',
      'komunitas basis',
      'kbg',
      'kring',
      'rukun',
    ],
    contoh: 'St. Maria',
  },
  {
    id: 'kepala_keluarga',
    label: label.kepala_keluarga,
    wajib: false,
    format: 'nama',
    alias: [
      'kepala keluarga',
      'nama kepala keluarga',
      'nama kk',
      'kepala',
      'nama kepala',
      'kepala rumah tangga',
      'nama',
      'nama lengkap',
    ],
    contoh: 'Yohanes Yufuwai',
  },
  {
    id: 'alamat',
    label: label.alamat,
    wajib: false,
    format: 'teks',
    alias: ['alamat', 'alamat rumah', 'alamat lengkap', 'jalan', 'jl'],
    contoh: 'Jl. Yos Sudarso 12',
  },
  {
    id: 'rt_rw',
    label: label.rt_rw,
    wajib: false,
    format: 'teks',
    alias: ['rt rw', 'rt', 'rw'],
    contoh: '03/01',
  },
  {
    id: 'kampung_desa',
    label: label.kampung_desa,
    wajib: false,
    format: 'teks',
    alias: ['kampung', 'desa', 'kampung desa', 'desa kelurahan', 'kelurahan', 'kel', 'dusun'],
    contoh: 'Bis Agats',
  },
  {
    id: 'kecamatan',
    label: label.kecamatan,
    wajib: false,
    format: 'teks',
    alias: ['distrik', 'kecamatan', 'kec'],
    contoh: 'Agats',
  },
  {
    id: 'kabupaten_kota',
    label: label.kabupaten_kota,
    wajib: false,
    format: 'teks',
    alias: ['kabupaten kota', 'kabupaten', 'kota', 'kab', 'kab kota'],
    contoh: 'Asmat',
  },
  {
    id: 'provinsi',
    label: label.provinsi,
    wajib: false,
    format: 'teks',
    alias: ['provinsi', 'propinsi', 'prov'],
    contoh: 'Papua Selatan',
  },
  {
    id: 'kode_pos',
    label: label.kode_pos,
    wajib: false,
    format: 'teks',
    alias: ['kode pos', 'kodepos', 'kd pos'],
    contoh: '99777',
  },
  {
    id: 'tanggal_kk',
    label: label.tanggal_kk,
    wajib: false,
    format: 'tanggal',
    alias: [
      'tanggal kk',
      'tgl kk',
      'tanggal kk dikeluarkan',
      'tgl kk dikeluarkan',
      'tanggal dikeluarkan',
      'dikeluarkan tanggal',
      'tanggal terbit kk',
    ],
    contoh: '12-03-2019',
  },
  {
    id: 'telepon',
    label: label.telepon,
    wajib: false,
    format: 'telepon',
    alias: TELEPON_ALIAS,
    contoh: '081244556677',
  },
  STATUS_RUMAH_KOLOM,
  {
    id: 'tanggal_mulai',
    label: label.tanggal_mulai,
    wajib: false,
    format: 'tanggal',
    alias: [
      'tanggal terdaftar',
      'tgl terdaftar',
      'terdaftar',
      'tanggal daftar',
      'tgl daftar',
      'tanggal mulai',
      'tgl mulai',
      'tanggal masuk',
      'tgl masuk',
      'tahun masuk',
      'sejak',
      'terdaftar sejak',
    ],
    contoh: '14-03-2019',
  },
  {
    id: 'catatan',
    label: label.catatan,
    wajib: false,
    format: 'teks',
    alias: CATATAN_ALIAS,
    contoh: '',
  },
]

export const KOLOM_UMAT: readonly KolomTarget[] = [
  {
    id: 'nama_lengkap',
    label: label.nama_lengkap,
    wajib: true,
    format: 'nama',
    alias: [
      'nama',
      'nama lengkap',
      'nama umat',
      'nama anggota',
      'nama anggota keluarga',
      'nama lengkap termasuk nama baptis',
      'nama diri',
      'name',
      'full name',
    ],
    contoh: 'Yohanes Yufuwai',
  },
  {
    // The family the person joins: its government number, its code, or its parish number.
    id: 'nomor_kk',
    label: label.nomor_kk,
    wajib: true,
    format: 'nomorKk',
    alias: [...NOMOR_KK_ALIAS, ...KODE_ALIAS],
    // Never 16 digits in a template (no NIK-like example): the format column says it.
    contoh: '16 angka',
  },
  {
    id: 'hubungan',
    label: label.hubungan,
    wajib: false,
    format: 'hubungan',
    alias: [
      'hubungan',
      'hubungan keluarga',
      'hubungan dalam keluarga',
      'hub keluarga',
      'hub',
      'shdk',
      'status hubungan',
      'status hubungan dalam keluarga',
      'status dalam keluarga',
      'hubungan dengan kk',
      'hubungan dengan kepala keluarga',
      'kedudukan',
      'posisi',
      'peran',
      'status hubungan keluarga',
    ],
    contoh: 'Kepala keluarga',
  },
  {
    // Release 19; a hubungan of "Anak 2" gives it too (`anakKeDari`).
    id: 'anak_ke',
    label: label.anak_ke,
    wajib: false,
    format: 'anakKe',
    alias: ['anak ke', 'anak keberapa', 'anak ke berapa', 'urutan anak', 'anak nomor'],
    contoh: '1',
  },
  {
    id: 'nama_baptis',
    label: label.nama_baptis,
    wajib: false,
    format: 'nama',
    alias: [
      'nama baptis',
      'baptis',
      'nama permandian',
      'nama kristen',
      'nama santo',
      'nama santa',
      'nama pelindung',
      'santo pelindung',
    ],
    contoh: 'Yohanes',
  },
  {
    id: 'nama_panggilan',
    label: label.nama_panggilan,
    wajib: false,
    format: 'nama',
    alias: ['nama panggilan', 'panggilan', 'nickname'],
    contoh: '',
  },
  {
    id: 'jenis_kelamin',
    label: label.jenis_kelamin,
    wajib: false,
    format: 'lp',
    alias: ['jenis kelamin', 'l p', 'jk', 'kelamin', 'gender', 'sex', 'lp', 'jkl'],
    contoh: 'L',
  },
  {
    id: 'tempat_lahir',
    label: label.tempat_lahir,
    wajib: false,
    format: 'teks',
    alias: [
      'tempat lahir',
      'tmp lahir',
      'tpt lahir',
      'kota lahir',
      'lahir di',
      'tempat',
      'tempat kelahiran',
    ],
    contoh: 'Agats',
  },
  {
    id: 'tanggal_lahir',
    label: label.tanggal_lahir,
    wajib: false,
    format: 'tanggal',
    alias: [
      'tanggal lahir',
      'tgl lahir',
      'tgl lhr',
      'tanggal lhr',
      'lahir',
      'tanggal bulan tahun lahir',
      'tanggal kelahiran',
      'tgl kelahiran',
      'tahun lahir',
      'thn lahir',
      'birth date',
      'date of birth',
      'dob',
    ],
    contoh: '12-05-1987',
  },
  {
    id: 'tempat_tanggal_lahir',
    label: label.tempat_tanggal_lahir,
    wajib: false,
    format: 'tempatTanggalLahir',
    alias: [
      'ttl',
      'tempat tanggal lahir',
      'tempat tgl lahir',
      'tempat dan tanggal lahir',
      'tempat tanggal lhr',
    ],
    contoh: 'Agats, 12-05-1987',
  },
  {
    id: 'nik',
    label: label.nik,
    wajib: false,
    format: 'angka16',
    alias: ['nik', 'no nik', 'nomor nik', 'no ktp', 'nomor ktp', 'ktp', 'nomor induk kependudukan'],
    contoh: '16 angka',
  },
  {
    id: 'nomor_akta_lahir',
    label: label.nomor_akta_lahir,
    wajib: false,
    format: 'teks',
    // No bare "no akta": as contained words it would claim "No Akta Baptis" or "No Akta Nikah".
    alias: [
      'nomor akta lahir',
      'no akta lahir',
      'akta lahir',
      'nomor akta kelahiran',
      'no akta kelahiran',
      'akta kelahiran',
    ],
    contoh: '9101-LT-12052011-0042',
  },
  {
    id: 'status_gereja',
    label: label.status_gereja,
    wajib: false,
    format: 'statusGereja',
    // Release 19: "Agama" is the religion's own column (the parish data sheet's heading).
    alias: [
      'status gereja',
      'status katolik',
      'katolik katekumen',
      'keanggotaan gereja',
      'status umat',
      'status keagamaan',
    ],
    contoh: 'Katolik',
  },
  {
    // Release 19: the religion; a non-Catholic spouse's other than Katolik, which makes them
    // "Bukan Katolik" when no Status gereja column says otherwise.
    id: 'agama',
    label: label.agama,
    wajib: false,
    format: 'agama',
    alias: ['agama', 'agama saat ini', 'agama sekarang', 'religion'],
    contoh: 'Katolik',
  },
  {
    id: 'agama_sebelumnya',
    label: label.agama_sebelumnya,
    wajib: false,
    format: 'agama',
    alias: [
      'agama sebelumnya',
      'agama lama',
      'agama asal',
      'agama dahulu',
      'agama terdahulu',
      'agama sebelum katolik',
    ],
    contoh: '',
  },
  {
    // The church or denomination; the Google Form asks a Protestant for their church.
    id: 'gereja_asal',
    label: label.gereja_asal,
    wajib: false,
    format: 'teks',
    alias: [
      'gereja asal',
      'asal gereja',
      'gereja sebelumnya',
      'denominasi',
      'nama gereja anda',
      'jika beragama protestan sebutkan nama gereja anda',
    ],
    contoh: '',
  },
  {
    id: 'status_perkawinan',
    label: label.status_perkawinan,
    wajib: false,
    format: 'statusPerkawinan',
    alias: [
      'status perkawinan',
      'status kawin',
      'perkawinan',
      'status pernikahan',
      'pernikahan',
      'status nikah',
      'kawin',
      'status',
    ],
    contoh: 'Menikah di Gereja',
  },
  {
    id: 'pendidikan',
    label: label.pendidikan,
    wajib: false,
    format: 'kodePendidikan',
    alias: [
      'pendidikan',
      'pendidikan terakhir',
      'pendidikan akhir',
      'pend',
      'pend terakhir',
      'tingkat pendidikan',
      'sekolah',
      'ijazah',
    ],
    contoh: 'SLTA/Sederajat',
  },
  {
    // Release 19: the school attended now; a cell "SMA Negeri 1 Agats" gives Nama sekolah too.
    id: 'pendidikan_saat_ini',
    label: label.pendidikan_saat_ini,
    wajib: false,
    format: 'pendidikanSaatIni',
    alias: [
      'pendidikan saat ini',
      'pendidikan sekarang',
      'sekolah saat ini',
      'sekolah sekarang',
      'masih sekolah',
      'sedang sekolah',
    ],
    contoh: '',
  },
  {
    id: 'jurusan',
    label: label.jurusan,
    wajib: false,
    format: 'teks',
    alias: ['jurusan', 'program studi', 'prodi', 'bidang studi'],
    contoh: '',
  },
  {
    id: 'nama_sekolah',
    label: label.nama_sekolah,
    wajib: false,
    format: 'teks',
    alias: ['nama sekolah', 'nama kampus', 'nama sekolah kampus', 'asal sekolah'],
    contoh: '',
  },
  {
    id: 'tahun_lulus',
    label: label.tahun_lulus,
    wajib: false,
    format: 'tahun',
    alias: ['tahun lulus', 'thn lulus', 'tahun kelulusan', 'lulus tahun', 'tahun tamat'],
    contoh: '2005',
  },
  {
    id: 'pekerjaan',
    label: label.pekerjaan,
    wajib: false,
    format: 'kodePekerjaan',
    // Release 19: "Profesi" is its own column now.
    alias: [
      'pekerjaan',
      'pekerjaan utama',
      'kerja',
      'jenis pekerjaan',
      'mata pencaharian',
      'job',
      'occupation',
    ],
    contoh: 'Petani/Pekebun',
  },
  {
    id: 'profesi',
    label: label.profesi,
    wajib: false,
    format: 'teks',
    alias: ['profesi', 'bidang profesi'],
    contoh: '',
  },
  {
    id: 'keterampilan',
    label: label.keterampilan,
    wajib: false,
    format: 'keterampilan',
    alias: [
      'keterampilan',
      'ketrampilan',
      'keahlian',
      'keahlian keterampilan',
      'keahlian ketrampilan',
      'skill',
      'skills',
    ],
    contoh: '',
  },
  {
    id: 'anggota_cu',
    label: label.anggota_cu,
    wajib: false,
    format: 'anggotaCu',
    alias: [
      'anggota cu',
      'anggota credit union',
      'credit union',
      'cu',
      'koperasi cu',
      'anggota credit union ndar sesepok',
    ],
    contoh: '',
  },
  {
    id: 'telepon',
    label: label.telepon,
    wajib: false,
    format: 'telepon',
    alias: TELEPON_ALIAS,
    contoh: '081244556677',
  },
  {
    id: 'email',
    label: label.email,
    wajib: false,
    format: 'email',
    alias: ['email', 'e mail', 'surel', 'alamat email'],
    contoh: '',
  },
  {
    id: 'nama_ayah',
    label: label.nama_ayah,
    wajib: false,
    format: 'nama',
    alias: [
      'nama ayah',
      'ayah',
      'bapak',
      'nama bapak',
      'father',
      'nama orang tua laki laki',
      'nama ayah kandung',
    ],
    contoh: 'Petrus Yufuwai',
  },
  {
    id: 'nama_ibu',
    label: label.nama_ibu,
    wajib: false,
    format: 'nama',
    alias: ['nama ibu', 'ibu', 'mother', 'nama orang tua perempuan', 'nama ibu kandung'],
    contoh: 'Maria Bewer',
  },
  {
    id: 'status_hidup',
    label: label.status_hidup,
    wajib: false,
    format: 'statusHidup',
    alias: ['status hidup', 'hidup meninggal', 'keadaan', 'hidup', 'status kehidupan'],
    contoh: 'Hidup',
  },
  {
    id: 'tanggal_meninggal',
    label: label.tanggal_meninggal,
    wajib: false,
    format: 'tanggal',
    alias: [
      'tanggal meninggal',
      'tgl meninggal',
      'tanggal wafat',
      'tgl wafat',
      'wafat',
      'meninggal',
    ],
    contoh: '',
  },
  ...kolomSakramen('baptis'),
  ...kolomSakramen('komuni'),
  ...kolomSakramen('krisma'),
  ...kolomSakramen('perkawinan'),
  // The person's family's housing (written to the family).
  STATUS_RUMAH_KOLOM,
  // Release 19: sensitive (`umat_profil`), read and written for parish staff and admins only.
  {
    id: 'golongan_darah',
    label: label.golongan_darah,
    wajib: false,
    format: 'golonganDarah',
    alias: ['golongan darah', 'gol darah', 'goldar', 'darah'],
    contoh: 'O',
    khususStaf: true,
  },
  {
    id: 'suku',
    label: label.suku,
    wajib: false,
    format: 'suku',
    alias: ['suku', 'ras suku', 'suku ras', 'suku bangsa', 'etnis', 'ras', 'asal suku'],
    contoh: 'Asmat',
    khususStaf: true,
  },
  {
    id: 'pendapatan_bulanan',
    label: label.pendapatan_bulanan,
    wajib: false,
    format: 'pendapatan',
    alias: [
      'pendapatan per bulan',
      'pendapatan perbulan',
      'pendapatan bulanan',
      'pendapatan',
      'penghasilan',
      'penghasilan per bulan',
      'penghasilan perbulan',
      'gaji',
      'income',
    ],
    contoh: '1–2 jt',
    khususStaf: true,
  },
  {
    id: 'asuransi_kesehatan',
    label: label.asuransi_kesehatan,
    wajib: false,
    format: 'asuransi',
    alias: ['asuransi kesehatan', 'asuransi', 'jaminan kesehatan', 'bpjs', 'bpjs kesehatan', 'jkn'],
    contoh: 'BPJS Kelas 3',
    khususStaf: true,
  },
  {
    id: 'catatan',
    label: label.catatan,
    wajib: false,
    format: 'teks',
    alias: CATATAN_ALIAS,
    contoh: '',
  },
]

const dalamSebutan = new WeakMap<readonly KolomTarget[], Map<string, readonly KolomTarget[]>>()

/**
 * Columns in the parish's word (Sebutan unit terkecil): the lingkungan column of a parish that
 * says Kombas is labelled "Kombas" (the template's heading, the mapping step, the sentences) and
 * a header "Kombas" maps to it. The same array per word, for the screens' memos.
 */
export function kolomDalamSebutan(
  kolom: readonly KolomTarget[],
  sebutan: SebutanUnit = SEBUTAN_BAWAAN,
): readonly KolomTarget[] {
  let perKata = dalamSebutan.get(kolom)
  if (perKata === undefined) {
    perKata = new Map()
    dalamSebutan.set(kolom, perKata)
  }
  const ada = perKata.get(sebutan.unit)
  if (ada !== undefined) return ada
  const hasil = kolom.map((k) => {
    if (!k.label.includes('{unit') && !k.contoh.includes('{unit')) return k
    const labelKata = isiUnit(k.label, sebutan)
    const kunci = kunciTeks(labelKata)
    return {
      ...k,
      label: labelKata,
      contoh: isiUnit(k.contoh, sebutan),
      alias: k.alias.includes(kunci) ? k.alias : [...k.alias, kunci],
    }
  })
  perKata.set(sebutan.unit, hasil)
  return hasil
}

export function kolomTarget(
  jenis: JenisImpor,
  sebutan: SebutanUnit = SEBUTAN_BAWAAN,
): readonly KolomTarget[] {
  return kolomDalamSebutan(jenis === 'keluarga' ? KOLOM_KELUARGA : KOLOM_UMAT, sebutan)
}

export function kolomById(jenis: JenisImpor, id: string): KolomTarget | undefined {
  return kolomTarget(jenis).find((k) => k.id === id)
}

// ---------------------------------------------------------------------------
// Headers
// ---------------------------------------------------------------------------

/** Header cells trimmed, the BOM dropped, blanks named, repeats numbered. */
export function rapikanHeader(header: readonly string[]): string[] {
  const seen = new Map<string, number>()
  return header.map((h, i) => {
    let text = h
      .replace(/^\uFEFF/, '')
      .replace(/\s+/g, ' ')
      .trim()
    if (text === '') text = `Kolom ${i + 1}`
    const n = seen.get(text.toLowerCase()) ?? 0
    seen.set(text.toLowerCase(), n + 1)
    return n === 0 ? text : `${text} (${n + 1})`
  })
}

/** The alias key of a header ("Tgl. Lahir" and "TGL LAHIR" share one). */
export function kunciHeader(header: string): string {
  return kunciTeks(header.replace(/\s\(\d+\)$/, ''))
}

/** Words that make a header a date's ("Tgl Baptis", "Tahun Krisma"). */
const KATA_TANGGAL: ReadonlySet<string> = new Set(['tgl', 'tanggal', 'tahun', 'thn', 'date'])

const kolomTanggal = (t: KolomTarget): boolean =>
  t.format === 'tanggal' || t.format === 'tempatTanggalLahir' || t.format === 'tahun'

/**
 * The first words of a column of uploaded scans ("Upload KTP/KIA", "Unggah Surat Baptis"): never
 * guessed. Scans are read, then deleted (owner, 2026-10-08); the cell is a link, never a value.
 */
const KATA_UNGGAHAN: ReadonlySet<string> = new Set([
  'upload',
  'unggah',
  'unggahan',
  'lampiran',
  'scan',
  'pindaian',
  'foto',
  'file',
  'berkas',
])

const unggahan = (key: string): boolean => KATA_UNGGAHAN.has(key.split(' ')[0] ?? '')

/**
 * Pair file columns with SATU columns by header: an exact alias key first, then the
 * longest alias that appears in the header as whole words, where a header with a date word
 * only ever goes to a date column ("Tgl Baptis" is never Nama baptis). Each SATU column is
 * taken once, by the first file column that claims it; the rest are skipped.
 */
export function tebakPemetaanDari(
  header: readonly string[],
  targets: readonly KolomTarget[],
): Mapping[] {
  const taken = new Set<string>()
  const out: Mapping[] = header.map((h) => ({ fileColumn: h, target: LEWATI }))
  const keys = header.map(kunciHeader)

  // Exact matches first so "Nama" goes to the head of the family before "Nama Ayah" claims "nama".
  keys.forEach((key, i) => {
    if (unggahan(key)) return
    const target = targets.find((t) => !taken.has(t.id) && t.alias.includes(key))
    const slot = out[i]
    if (target !== undefined && slot !== undefined) {
      slot.target = target.id
      taken.add(target.id)
    }
  })
  keys.forEach((key, i) => {
    const slot = out[i]
    if (slot === undefined || slot.target !== LEWATI || key === '' || unggahan(key)) return
    const tanggal = key.split(' ').some((w) => KATA_TANGGAL.has(w))
    let best: { target: KolomTarget; alias: string } | null = null
    for (const target of targets) {
      if (taken.has(target.id) || (tanggal && !kolomTanggal(target))) continue
      for (const alias of target.alias) {
        if (alias.length < 3) continue
        if (!` ${key} `.includes(` ${alias} `)) continue
        if (best === null || alias.length > best.alias.length) best = { target, alias }
      }
    }
    if (best !== null) {
      slot.target = best.target.id
      taken.add(best.target.id)
    }
  })
  return out
}

/** `tebakPemetaanDari` over an Impor template's columns (the parish's word a header too). */
export function tebakPemetaan(
  header: readonly string[],
  jenis: JenisImpor,
  sebutan: SebutanUnit = SEBUTAN_BAWAAN,
): Mapping[] {
  return tebakPemetaanDari(header, kolomTarget(jenis, sebutan))
}

/**
 * The identity of a header set, for remembering its mapping across files: `jenis` is Impor's
 * template, or `upacara_<tabel>` for a ceremony batch.
 */
export function kunciPemetaan(header: readonly string[], jenis: string): string {
  return `${jenis}|${header.map(kunciHeader).join('|')}`
}

/**
 * The headers a column's alias names exactly: all a remembered mapping may hold. A file whose
 * first row is data (a name, a NIK) has none of its values recognised, so none is ever kept.
 */
export function headerDikenal(
  header: readonly string[],
  targets: readonly KolomTarget[],
): string[] {
  return header.filter((h) => {
    const key = kunciHeader(h)
    return key !== '' && targets.some((t) => t.alias.includes(key))
  })
}

/**
 * A mapping to remember on the device: its key and its pairs over the recognised headers only
 * (`headerDikenal`); null when no header is recognised. Read back by the same key,
 * `kunciPemetaan(headerDikenal(header, targets), jenis)`.
 */
export function pemetaanUntukDiingat(
  header: readonly string[],
  pemetaan: readonly Mapping[],
  targets: readonly KolomTarget[],
  jenis: string,
): { kunci: string; pemetaan: Mapping[] } | null {
  const dikenal = headerDikenal(header, targets)
  if (dikenal.length === 0) return null
  const ada = new Set(dikenal)
  return {
    kunci: kunciPemetaan(dikenal, jenis),
    pemetaan: pemetaan.filter((m) => ada.has(m.fileColumn)),
  }
}

/**
 * A remembered mapping applied to the same headers: remembered pairs win where the file
 * column still exists and the target still exists; anything else falls back to the guess.
 */
export function gabungPemetaanDari(
  tebakan: readonly Mapping[],
  tersimpan: readonly Mapping[] | undefined,
  targets: readonly KolomTarget[],
): Mapping[] {
  if (tersimpan === undefined) return [...tebakan]
  const valid = new Set(targets.map((k) => k.id))
  const remembered = new Map(
    tersimpan
      .filter((m) => m.target === LEWATI || valid.has(m.target))
      .map((m) => [kunciHeader(m.fileColumn), m.target] as const),
  )
  const taken = new Set<string>()
  const out = tebakan.map((m) => {
    const target = remembered.get(kunciHeader(m.fileColumn))
    if (target === undefined) return { ...m }
    if (target !== LEWATI && taken.has(target)) return { fileColumn: m.fileColumn, target: LEWATI }
    if (target !== LEWATI) taken.add(target)
    return { fileColumn: m.fileColumn, target }
  })
  // A guessed target that a remembered pair did not use stays with its guess.
  for (const m of out) {
    if (m.target !== LEWATI && !taken.has(m.target)) taken.add(m.target)
    else if (m.target !== LEWATI && remembered.get(kunciHeader(m.fileColumn)) === undefined)
      m.target = LEWATI
  }
  return out
}

/** `gabungPemetaanDari` over an Impor template's columns. */
export function gabungPemetaan(
  tebakan: readonly Mapping[],
  tersimpan: readonly Mapping[] | undefined,
  jenis: JenisImpor,
  sebutan: SebutanUnit = SEBUTAN_BAWAAN,
): Mapping[] {
  return gabungPemetaanDari(tebakan, tersimpan, kolomTarget(jenis, sebutan))
}

export interface PemeriksaanPemetaan {
  /** Required SATU columns with no file column. */
  wajibBelum: KolomTarget[]
  /** SATU columns given to two file columns. */
  duaKali: KolomTarget[]
  /** File columns that are imported. */
  terpetakan: number
  lengkap: boolean
}

export function periksaPemetaanDari(
  mapping: readonly Mapping[],
  targets: readonly KolomTarget[],
): PemeriksaanPemetaan {
  const counts = new Map<string, number>()
  for (const m of mapping) {
    if (m.target === LEWATI) continue
    counts.set(m.target, (counts.get(m.target) ?? 0) + 1)
  }
  const ada = (id: string) => (counts.get(id) ?? 0) > 0
  const wajibBelum = targets.filter((t) => {
    if (!t.wajib) return false
    // A combined "Tempat, tanggal lahir" column does not stand in for a required one, but
    // nothing required is covered by a combination today; kept for the umat template's
    // birth pair, which is optional.
    return !ada(t.id)
  })
  const duaKali = targets.filter((t) => (counts.get(t.id) ?? 0) > 1)
  const terpetakan = mapping.filter((m) => m.target !== LEWATI).length
  return {
    wajibBelum,
    duaKali,
    terpetakan,
    lengkap: wajibBelum.length === 0 && duaKali.length === 0,
  }
}

/** `periksaPemetaanDari` over an Impor template's columns. */
export function periksaPemetaan(
  mapping: readonly Mapping[],
  jenis: JenisImpor,
  sebutan: SebutanUnit = SEBUTAN_BAWAAN,
  opsi: { tanpaKk?: boolean } = {},
): PemeriksaanPemetaan {
  const targets = kolomTarget(jenis, sebutan)
  // Release 18: "Belum punya KK Disdukcapil" ticked, a family file needs no Nomor KK column.
  return periksaPemetaanDari(
    mapping,
    jenis === 'keluarga' && opsi.tanpaKk === true
      ? targets.map((t) => (t.id === 'nomor_kk' ? { ...t, wajib: false } : t))
      : targets,
  )
}

/** Which template a file's headers look like: umat when they carry a person's columns. */
export function tebakJenis(header: readonly string[]): JenisImpor | null {
  const umat = tebakPemetaan(header, 'umat')
  const keluarga = tebakPemetaan(header, 'keluarga')
  const hasUmat = umat.some((m) =>
    ['tanggal_lahir', 'jenis_kelamin', 'hubungan', 'nik', 'tempat_tanggal_lahir'].includes(
      m.target,
    ),
  )
  const hasKeluarga = keluarga.some((m) => ['alamat', 'kampung_desa', 'rt_rw'].includes(m.target))
  if (hasUmat && !hasKeluarga) return 'umat'
  if (hasKeluarga && !hasUmat) return 'keluarga'
  if (hasUmat && hasKeluarga) return 'umat'
  return null
}

/** The raw cell of a target column in a row, given the mapping; null when unmapped. */
export function nilaiKolom(
  row: readonly string[],
  header: readonly string[],
  mapping: readonly Mapping[],
  target: string,
): string | null {
  const m = mapping.find((x) => x.target === target)
  if (m === undefined) return null
  const index = header.indexOf(m.fileColumn)
  if (index === -1) return null
  return row[index] ?? ''
}

/** Every mapped target of a row as raw text, keyed by target id. */
export function nilaiBaris(
  row: readonly string[],
  header: readonly string[],
  mapping: readonly Mapping[],
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const m of mapping) {
    if (m.target === LEWATI) continue
    const index = header.indexOf(m.fileColumn)
    if (index === -1) continue
    out[m.target] = row[index] ?? ''
  }
  return out
}
